/**
 * useUserPlan — reads the current user's plan from the user_plans table.
 *
 * Returns:
 *  - plan: 'free' | 'pro' (defaults to 'free' if no row exists)
 *  - isPro: boolean shorthand
 *  - isLoading: true while the query is in flight
 *  - currentPeriodEnd: Date | null — when the Pro subscription renews/expires
 *  - cancelAtPeriodEnd: boolean — true if cancelled but still in paid period
 *
 * Design: read-only from the client. All writes go through the Stripe webhook
 * handler (service role). This prevents users from upgrading themselves for free.
 */
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/supabaseClient";

export type Plan = "free" | "pro";

export interface UserPlan {
  plan: Plan;
  isPro: boolean;
  isLoading: boolean;
  currentPeriodEnd: Date | null;
  cancelAtPeriodEnd: boolean;
  stripeCustomerId: string | null;
}

const FREE_PLAN: UserPlan = {
  plan: "free",
  isPro: false,
  isLoading: false,
  currentPeriodEnd: null,
  cancelAtPeriodEnd: false,
  stripeCustomerId: null,
};

export function useUserPlan(): UserPlan {
  const { user } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ["user_plan", user?.id],
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes — plan changes are infrequent
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_plans")
        .select(
          "plan, current_period_end, cancel_at_period_end, stripe_customer_id",
        )
        .eq("user_id", user!.id)
        .maybeSingle();

      if (error) {
        console.error("[useUserPlan] error fetching plan:", error.message);
        return null;
      }
      return data;
    },
  });

  if (isLoading) {
    return { ...FREE_PLAN, isLoading: true };
  }

  if (!data) {
    return FREE_PLAN;
  }

  const plan = (data.plan as Plan) ?? "free";
  return {
    plan,
    isPro: plan === "pro",
    isLoading: false,
    currentPeriodEnd: data.current_period_end
      ? new Date(data.current_period_end)
      : null,
    cancelAtPeriodEnd: data.cancel_at_period_end ?? false,
    stripeCustomerId: data.stripe_customer_id ?? null,
  };
}

/** The maximum number of active subscriptions allowed on the free plan. */
export const FREE_PLAN_SUBSCRIPTION_LIMIT = 10;
